import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

function clampPage(page, totalPages) {
    const safePage = Number.isFinite(page) ? Math.floor(page) : 1
    return Math.min(Math.max(safePage, 1), totalPages)
}

export function usePagination({
    totalItems = 0,
    limit = 9,
    initialPage = 1,
    page: controlledPage,
    onPageChange,
    resetKey,
}) {
    const isControlled = typeof controlledPage === 'number'
    const [internalPage, setInternalPage] = useState(initialPage)

    const totalPages = useMemo(() => {
        if (!limit || limit <= 0) return 1
        return Math.max(1, Math.ceil(totalItems / limit))
    }, [limit, totalItems])

    const currentRawPage = isControlled ? controlledPage : internalPage
    const page = clampPage(currentRawPage, totalPages)

    useEffect(() => {
        if (!isControlled) {
            setInternalPage((prev) => clampPage(prev, totalPages))
        }
    }, [isControlled, totalPages])

    const setPage = useCallback(
        (nextPage) => {
            const resolvedPage = typeof nextPage === 'function' ? nextPage(page) : nextPage
            const clampedPage = clampPage(resolvedPage, totalPages)
            if (isControlled) {
                onPageChange?.(clampedPage)
                return
            }
            setInternalPage(clampedPage)
            onPageChange?.(clampedPage)
        },
        [isControlled, onPageChange, page, totalPages],
    )

    const goPrev = useCallback(() => {
        setPage((prev) => prev - 1)
    }, [setPage])

    const goNext = useCallback(() => {
        setPage((prev) => prev + 1)
    }, [setPage])

    const resetPage = useCallback(() => {
        setPage(1)
    }, [setPage])

    const prevResetKeyRef = useRef(resetKey)
    useEffect(() => {
        if (resetKey !== undefined && resetKey !== prevResetKeyRef.current) {
            setPage(1)
        }
        prevResetKeyRef.current = resetKey
    }, [resetKey, setPage])

    const canPrev = page > 1
    const canNext = page < totalPages
    const startIndex = (page - 1) * limit
    const endIndex = startIndex + limit

    return {
        page,
        totalPages,
        canPrev,
        canNext,
        startIndex,
        endIndex,
        setPage,
        goPrev,
        goNext,
        resetPage,
    }
}

